// Patient Service - Patient CRUD operations with local storage fallback
import api from './api';

// ─── Local storage helpers ───────────────────────────────────────────
const LOCAL_KEY = 'vivalyn_local_patients';

function getLocalPatients() {
  try {
    const stored = localStorage.getItem(LOCAL_KEY);
    if (stored) return JSON.parse(stored);
  } catch { /* ignore */ }
  return [];
}

function saveLocalPatients(patients) {
  localStorage.setItem(LOCAL_KEY, JSON.stringify(patients));
}

// ─── Service ─────────────────────────────────────────────────────────
export const patientService = {
  // List patients with pagination and search
  async getPatients({ page = 1, pageSize = 20, search = '', status = '' } = {}) {
    try {
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('page_size', pageSize);
      if (search) params.append('search', search);
      if (status) params.append('status', status);

      const response = await api.get(`/api/v1/patients?${params.toString()}`);
      const apiPatients = (response.data.patients || []).map(p => ({
        ...p,
        bloodGroup: p.blood_group || p.bloodGroup || '',
        blood_group: p.blood_group || p.bloodGroup || '',
        dateOfBirth: p.date_of_birth || p.dateOfBirth || '',
        lastVisit: p.last_visit || p.lastVisit || null,
        status: p.status ? p.status.charAt(0).toUpperCase() + p.status.slice(1).toLowerCase() : 'Active',
      }));
      return {
        success: true,
        data: apiPatients,
        total: response.data.total,
        page: response.data.page,
        pageSize: response.data.page_size,
      };
    } catch (err) {
      console.warn('getPatients API failed, using local fallback:', err.message);
      // Fallback to local data
      let patients = getLocalPatients();
      if (search) {
        const term = search.toLowerCase();
        patients = patients.filter(p =>
          p.name.toLowerCase().includes(term) ||
          p.mrn.toLowerCase().includes(term) ||
          (p.phone && p.phone.includes(term))
        );
      }
      if (status) {
        patients = patients.filter(p => p.status.toLowerCase() === status.toLowerCase());
      }
      const start = (page - 1) * pageSize;
      const paged = patients.slice(start, start + pageSize);
      // Normalize fields so UI always gets both camelCase and snake_case
      const normalized = paged.map(p => ({
        ...p,
        bloodGroup: p.bloodGroup || p.blood_group || '',
        blood_group: p.blood_group || p.bloodGroup || '',
        dateOfBirth: p.dateOfBirth || p.date_of_birth || '',
        lastVisit: p.lastVisit || p.last_visit || null,
        status: p.status ? p.status.charAt(0).toUpperCase() + p.status.slice(1).toLowerCase() : 'Active',
      }));
      return { success: true, data: normalized, total: patients.length, page, pageSize };
    }
  },

  // Get single patient by ID
  async getPatient(patientId) {
    try {
      const response = await api.get(`/api/v1/patients/${patientId}`);
      const p = response.data;
      return { success: true, data: {
        ...p,
        bloodGroup: p.blood_group || p.bloodGroup || '',
        dateOfBirth: p.date_of_birth || p.dateOfBirth || '',
        lastVisit: p.last_visit || p.lastVisit || null,
        status: p.status ? p.status.charAt(0).toUpperCase() + p.status.slice(1).toLowerCase() : 'Active',
      }};
    } catch (err) {
      console.warn('getPatient API failed:', err.message);
      const patients = getLocalPatients();
      const patient = patients.find(p => p.id === patientId);
      if (patient) return { success: true, data: {
        ...patient,
        bloodGroup: patient.bloodGroup || patient.blood_group || '',
        dateOfBirth: patient.dateOfBirth || patient.date_of_birth || '',
      }};
      return { success: false, error: 'Patient not found' };
    }
  },

  // Create new patient
  async createPatient(patientData) {
    try {
      const response = await api.post('/api/v1/patients', patientData);
      return { success: true, data: response.data };
    } catch (err) {
      const msg = err.response?.data?.message || err.response?.data?.detail || err.message || 'Failed to create patient';
      console.error('createPatient API error:', msg);
      return { success: false, error: msg };
    }
  },

  // Update patient
  async updatePatient(patientId, patientData) {
    try {
      const response = await api.put(`/api/v1/patients/${patientId}`, patientData);
      return { success: true, data: response.data };
    } catch (err) {
      const msg = err.response?.data?.message || err.response?.data?.detail || err.message || 'Failed to update patient';
      console.error('updatePatient API error:', msg);
      return { success: false, error: msg };
    }
  },

  // Delete patient (soft delete)
  async deletePatient(patientId) {
    try {
      await api.delete(`/api/v1/patients/${patientId}`);
      return { success: true };
    } catch (err) {
      const msg = err.response?.data?.message || err.response?.data?.detail || err.message || 'Failed to delete patient';
      console.error('deletePatient API error:', msg);
      return { success: false, error: msg };
    }
  },
};

export default patientService;
